import { ScoringWell } from 'business/models/CustomModels';
import EScoreType from 'enums/EScoreType';
import { INTRESTINGSCOREID } from 'redux/reducers/plateReducer';

export const DELETE_SCORE_HOTKEY_BLOCKED_MESSAGE =
  'Deleting the scores using a hotkey is not allowed as per the current preferences.';

export type ScoringWellIdentity = {
  wellNumber: number;
  plateId: number;
  imageBatchId?: number;
  dropNumber: number;
};

export type ScoreMatchInput = {
  scoreBatchId: number;
  dropNumber: number;
  wellNumber: number;
  wellDropScoreGroupId: number;
  wellDropScoreId: number;
  scoreType: number;
};

export type BuildScoringWellsParams = {
  wellDropScoreId: number;
  wellDropScoreGroupId: number;
  wells: ScoringWellIdentity[];
  selectedWellScores: ScoreMatchInput[][];
  forceRemoveInteresting?: boolean;
};

export type ResolveScoringResult =
  | { blocked: true; message: string; scores: ScoringWell[] }
  | { blocked: false; scores: ScoringWell[] };

const isExistingManualScore = (
  selectedWellScores: ScoreMatchInput[][],
  well: ScoringWellIdentity,
  wellDropScoreId: number,
  wellDropScoreGroupId: number,
  forceRemoveInteresting?: boolean,
): boolean => {
  return selectedWellScores
    .flatMap((scores) =>
      scores.filter(
        (sc) =>
          sc.scoreBatchId === well.imageBatchId &&
          sc.dropNumber === well.dropNumber &&
          sc.wellNumber === well.wellNumber &&
          ((wellDropScoreGroupId === INTRESTINGSCOREID && forceRemoveInteresting) ||
            sc.scoreType !== EScoreType.Inherited),
      ),
    )
    .some(
      (score) =>
        score.wellDropScoreGroupId === wellDropScoreGroupId &&
        score.wellDropScoreId === wellDropScoreId,
    );
};

export const buildScoringWells = ({
  wellDropScoreId,
  wellDropScoreGroupId,
  wells,
  selectedWellScores,
  forceRemoveInteresting,
}: BuildScoringWellsParams): ScoringWell[] => {
  return wells.map((well) => ({
    wellNumber: well.wellNumber,
    plateId: well.plateId,
    wellDropScoreId,
    wellDropScoreGroupId,
    isAutoScore: false,
    imageBatchId: well.imageBatchId,
    dropNumber: well.dropNumber,
    scoreType: EScoreType.Manual,
    isDeleteScore: isExistingManualScore(
      selectedWellScores,
      well,
      wellDropScoreId,
      wellDropScoreGroupId,
      forceRemoveInteresting,
    ),
  }));
};

/** Unique key matching ImageBatchWellDropScore uniqueness (plate/batch/well/drop/score). */
const getScoringWellUniqueKey = (score: ScoringWell): string =>
  `${score.plateId}|${score.imageBatchId}|${score.wellNumber}|${score.dropNumber}|${score.wellDropScoreId}|${score.wellDropScoreGroupId}`;

/** SNF search rows can repeat the same plate/batch/well/drop; keep first occurrence only. */
const dedupeScoringWells = (scores: ScoringWell[]): ScoringWell[] => {
  const seen = new Set<string>();
  return scores.filter((score) => {
    const key = getScoringWellUniqueKey(score);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/** Mixed multi-well: keep adds only; single / all-delete / all-add keep as-is. */
export const filterMixedWellScores = (scores: ScoringWell[]): ScoringWell[] => {
  const filtered =
    scores.length <= 1 || scores.every((s) => s.isDeleteScore)
      ? scores
      : scores.filter((s) => !s.isDeleteScore);
  return dedupeScoringWells(filtered);
};

export const resolveScoringAction = ({
  scores,
  disAllowScoreRemove,
}: {
  scores: ScoringWell[];
  disAllowScoreRemove?: boolean;
}): ResolveScoringResult => {
  const filteredScores = filterMixedWellScores(scores);

  if (filteredScores.some((s) => s.isDeleteScore) && disAllowScoreRemove) {
    return {
      blocked: true,
      message: DELETE_SCORE_HOTKEY_BLOCKED_MESSAGE,
      scores: [],
    };
  }

  return { blocked: false, scores: filteredScores };
};
